import { IntlShape } from 'react-intl';
import type { Content } from '@plone/types';
import { InfoItemProps } from '@designsystem/stories/02-atoms/Info';
import { TagProps } from '@designsystem/stories/02-atoms/Tag';
import { TermProps } from '@fhnw/types//term';
import type { OccasionProps } from '@fhnw/types/occasion';
import { normalizeContent } from './normalizeContent';
import { addLineBreaksAsHTML } from '@fhnw/helpers/text/addLineBreaks';
import containsHTML from '@fhnw/helpers/text/containsHTML';
import { messages } from './messages';
import { NormalizableContent } from './types';

/**
 * Converts various value types to string or undefined
 */
export const stringOrUndefined = (
  value: string | string[] | TermProps | TermProps[] | undefined,
  intl: IntlShape,
  joinComma: boolean = false,
): string | undefined => {
  if (Array.isArray(value)) {
    const items = value.map((item) => stringOrUndefined(item, intl));

    if (joinComma) {
      const joined = items.filter(Boolean).join(', ');
      return joined || undefined;
    }

    const last = items.pop();
    const itemsString = items.join(', ');

    if (itemsString && last) {
      return intl.formatMessage(messages.InfoItemList, {
        items: itemsString,
        last: last,
      });
    }
    return last;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && value !== null && 'title' in value) {
    return value.title;
  }

  return undefined;
};

/**
 * Formats info items with proper HTML handling
 */
export const formatInfoItem = (
  item: InfoItemProps,
  intl: IntlShape,
  html: boolean = true,
  joinComma: boolean = false,
): InfoItemProps => {
  if (Array.isArray(item?.value)) {
    item.value = stringOrUndefined(item.value, intl, joinComma);
  } else if (typeof item?.value === 'string' && html) {
    if (!containsHTML(item.value)) {
      item.value = addLineBreaksAsHTML(item.value);
    }
    item.structured = true;
  } else if (
    typeof item?.value === 'object' &&
    item.value !== null &&
    'title' in item.value
  ) {
    item.value = stringOrUndefined(item.value as unknown as TermProps, intl);
  }
  return item;
};

/**
 * Checks if an occasion is currently visible based on date constraints
 * For FHNWEvent (Event Standard), occasion_start/occasion_end are ignored (events always visible)
 * but show_from/show_until constraints are still applied
 * For InfoEvent (Event Info-Anlass), the time of occasion_start is normalized to 23:59 of the start day
 */
export const isOccasionVisible = (
  occasion: OccasionProps,
  parentContent?: Content,
): boolean => {
  const now = new Date();
  const isEventStandard = parentContent?.['@type'] === 'FHNWEvent';

  // Check show_from constraint (applies to all content types)
  if (occasion?.show_from && new Date(occasion.show_from) > now) {
    return false;
  }

  // Check show_until constraint (applies to all content types)
  if (occasion?.show_until && new Date(occasion.show_until) < now) {
    return false;
  }

  // Rolling intake is always visible if within show constraints
  if (occasion?.rolling_intake) {
    return true;
  }

  // For FHNWEvent (Event Standard), ignore occasion_start/occasion_end
  // Events are always visible as long as show_from/show_until constraints are met
  if (isEventStandard) {
    return true;
  }

  // For other types (InfoEvent), check occasion start/end dates
  if (!occasion?.occasion_start) {
    return false;
  }

  const isInfoEvent = parentContent?.['@type'] === 'InfoEvent';
  const occasionStart = new Date(occasion.occasion_start);

  // For InfoEvent: normalize time to 23:59 of the start day
  // This keeps the card visible for the entire start day
  if (isInfoEvent) {
    const endOfStartDay = new Date(
      occasionStart.getFullYear(),
      occasionStart.getMonth(),
      occasionStart.getDate(),
      23,
      59,
      59,
      999,
    );
    if (endOfStartDay < now) {
      return false;
    }
  } else {
    // For other types: use exact start time
    if (occasionStart < now) {
      return false;
    }
  }

  // commented this out, because:
  // In the isInfoEvent case we want to show it till the end of the day
  // and otherwise we go by the exact start time, so the end time is not relevant as
  // there MUST be a start and the end time must be after the start.
  // if (occasion?.occasion_end && new Date(occasion.occasion_end) < now) {
  //  return false;
  // }

  return true;
};

/**
 * Finds the next visible occasion from a list
 * Optionally accepts parentContent for FHNWEvent visibility logic
 */
export const findNextOccasion = (
  occasions?: OccasionProps[],
  parentContent?: Content,
): OccasionProps | null => {
  if (!occasions || occasions.length === 0) {
    return null;
  }

  let nextOccasion: OccasionProps | null = null;

  for (const occasion of occasions) {
    if (!isOccasionVisible(occasion, parentContent)) {
      continue;
    }

    if (
      nextOccasion === null ||
      !occasion.occasion_start ||
      (nextOccasion.occasion_start &&
        nextOccasion.occasion_start > occasion.occasion_start)
    ) {
      nextOccasion = occasion;
    }
  }

  return nextOccasion;
};

/**
 * Extracts subject tags from content, filtering out machine tags
 */
export const extractSubjectTags = (item: NormalizableContent): TagProps[] => {
  const tags: TagProps[] = [];
  const subjects: string[] = item?.subjects || item?.Subject || [];

  for (const subject of subjects) {
    const label = normalizeContent(subject, true);

    // Skip machine tags (lowercase with underscores/spaces)
    if (typeof label === 'string' && label.match(/^[a-z][a-z_ ]+$/)) {
      continue;
    }

    if (typeof label === 'string' && label.trim()) {
      tags.push({
        label: label,
      });
    }
  }

  return tags;
};

/**
 * Creates a memoized cache for expensive operations
 */
export const createMemoCache = <T, R>(
  fn: (input: T) => R,
  keyFn: (input: T) => string = (input) => JSON.stringify(input),
): ((input: T) => R) => {
  const cache = new Map<string, R>();

  return (input: T): R => {
    const key = keyFn(input);

    if (cache.has(key)) {
      return cache.get(key)!;
    }

    const result = fn(input);
    cache.set(key, result);
    return result;
  };
};
